import { Box, Card, Stack, Typography } from "@compass/core";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import ExpandCircleDownIcon from "@mui/icons-material/ExpandCircleDown";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import { useState, createContext, useContext } from "react";
import { DraggableHandle } from "./DashboardGrid";
import { DASHBOARD_CARD_HEADER_SX } from "../styles/cardStyles";

// Context for card-level actions
export interface DashboardCardContextValue {
    onRemove?: () => void;
    onExpand?: () => void;
    isExpanded: boolean;
}

export const DashboardCardContext = createContext<DashboardCardContextValue>({
    isExpanded: false,
});

type Props = {
    title: string;
    children: React.ReactNode;
    rightActions?: React.ReactNode;
    showDragHandle?: boolean;
    onExport?: () => void;
};

export const DashboardCard: React.FC<Props> = ({ 
    title, 
    children, 
    rightActions, 
    showDragHandle = true,
    onExport,
}) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const menuOpen = Boolean(anchorEl);

    const { onRemove, onExpand, isExpanded } = useContext(DashboardCardContext);

    // Show menu if we have any actions available (and not in expanded mode)
    const hasMenuItems = Boolean(onRemove || onExport || onExpand);
    const showMenu = hasMenuItems && !isExpanded;

    const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleExpand = () => {
        if (onExpand) {
            onExpand();
        }
        handleMenuClose();
    };

    const handleExport = () => {
        if (onExport) {
            onExport();
        }
        handleMenuClose();
    };

    const handleRemove = () => {
        if (onRemove) {
            onRemove();
        }
        handleMenuClose();
    };

    // Determine if drag handle should be visible
    const shouldShowDragHandle = showDragHandle && !isExpanded;

    // When expanded, render only the content without the Card wrapper
    if (isExpanded) {
        return (
            <Box sx={{ width: '100%', height: '100%' }}>
                {children}
            </Box>
        );
    }

    return (
        <Box
            sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <Card
                header={
                    <Box
                        sx={{
                            ...DASHBOARD_CARD_HEADER_SX,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            userSelect: "none"
                        }}
                    >
                        <Stack direction="row" spacing="sm" alignItems="center">
                            {shouldShowDragHandle && (
                                <DraggableHandle>
                                    <DragIndicatorIcon style={{ color: "rgba(255,255,255,0.7)" }} />
                                </DraggableHandle>
                            )}
                            <Typography variant="h6">{title}</Typography>
                        </Stack>
                        <Stack direction="row" spacing="sm" alignItems="center">
                            {rightActions}
                            
                            {showMenu && (
                                <>
                                    <IconButton
                                        size="small"
                                        onClick={handleMenuClick}
                                        sx={{ color: "white" }} 
                                        aria-label="Card options"
                                        aria-controls={menuOpen ? "card-menu" : undefined}
                                        aria-haspopup="true"
                                        aria-expanded={menuOpen ? "true" : undefined}
                                    >
                                        <ExpandCircleDownIcon fontSize="small" />
                                    </IconButton>
                                    <Menu
                                        id="card-menu"
                                        anchorEl={anchorEl}
                                        open={menuOpen}
                                        onClose={handleMenuClose}
                                        MenuListProps={{
                                            "aria-labelledby": "card-options-button",
                                        }}
                                    >
                                        {onExpand && (
                                            <MenuItem onClick={handleExpand}>
                                                <ListItemIcon>
                                                    <OpenInFullIcon fontSize="small" />
                                                </ListItemIcon>
                                                <ListItemText>Expand</ListItemText>
                                            </MenuItem>
                                        )}

                                        {onExport && (
                                            <MenuItem onClick={handleExport}>
                                                <ListItemIcon>
                                                    <FileDownloadIcon fontSize="small" />
                                                </ListItemIcon>
                                                <ListItemText>Export</ListItemText>
                                            </MenuItem>
                                        )}

                                        {onRemove && (
                                            <MenuItem 
                                                onClick={handleRemove}
                                                sx={{ color: "error.main" }}
                                            >
                                                <ListItemIcon>
                                                    <DeleteOutlineIcon fontSize="small" sx={{ color: "error.main" }} />
                                                </ListItemIcon>
                                                <ListItemText>Remove</ListItemText>
                                            </MenuItem>
                                        )}
                                    </Menu>
                                </>
                            )}
                        </Stack>
                    </Box>
                }
                headerSx={DASHBOARD_CARD_HEADER_SX}
            >
                <Box
                    sx={{
                        height: 700,
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        pt: 1,
                        px: 2,
                    }}
                >
                    {children}
                </Box>
            </Card>
        </Box>
    );
};